import { useHideFields, useUnhideFields } from 'api/MappingsAPI';
import { useUpdateProjectSettings } from 'modules/dataDictionary/hooks';
import {
  createHideFieldsPayload,
  createSuccessMessage,
  createUnhideFieldsPayload,
} from 'modules/dataDictionary/utils';
import { DataTypeType } from 'modules/domains';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { Action as BulkAction } from 'ui/bulkActionsBar';
import { Hide16, View16 } from 'ui/icons';
import { FullWidth } from 'ui/utility/fullWidth';
import { StyledBulkActionsBar } from './TableActions.styled';

export const TABLE_BULK_ACTIONS_TEXT = {
  errorMessage: 'Oops! Something went wrong. Refresh the page and try again.',
  hideFieldsLabel: 'Hide',
  showFieldsLabel: 'Show',
};

const { errorMessage, hideFieldsLabel, showFieldsLabel } = TABLE_BULK_ACTIONS_TEXT;

export type TableBulkActionsProps = {
  dataType: DataTypeType;
  onSuccess: () => void;
  projectId: number;
  fields: (string | number)[];
};

export const TableBulkActions = ({
  projectId,
  dataType,
  onSuccess,
  fields,
}: TableBulkActionsProps) => {
  const dispatch = useDispatch();
  const { defaultDataSettings, updateSettings } = useUpdateProjectSettings();

  const { mutate: hideFields } = useHideFields({
    onError: (error) => {
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useHideFields', error));
    },
    onSuccess: () => {
      const showPayload = createHideFieldsPayload({ dataType, defaultDataSettings, fields });
      updateSettings(showPayload);
      dispatch(addPositiveToast(createSuccessMessage(fields.length, 'hidden')));
      onSuccess();
    },
  });

  const { mutate: unhideFields } = useUnhideFields({
    onError: (error) => {
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useUnhideFields', error));
    },
    onSuccess: () => {
      const hidePayload = createUnhideFieldsPayload({ dataType, defaultDataSettings, fields });
      updateSettings(hidePayload);
      dispatch(addPositiveToast(createSuccessMessage(fields.length, 'visible')));
      onSuccess();
    },
  });

  const bulkActions: BulkAction[] = [
    {
      icon: <View16 />,
      label: showFieldsLabel,
      onClick: () => unhideFields({ projectId, fields, dataType }),
      sortIndex: 0,
    },
    {
      icon: <Hide16 />,
      label: hideFieldsLabel,
      onClick: () => hideFields({ projectId, fields, dataType }),
      sortIndex: 0,
    },
  ];

  return (
    <>
      {fields.length > 0 && (
        <FullWidth>
          <StyledBulkActionsBar actions={bulkActions} numSelected={fields.length} />
        </FullWidth>
      )}
    </>
  );
};
